import { useEffect, useMemo, forwardRef, useImperativeHandle } from 'react';

// eslint-disable-next-line
import { useRowSelect, useSortBy, useTable } from 'react-table';

import {
  EmptyStateContainer,
  HeaderCellWithIcon,
  StyledEmptyStateText,
  StyledTable,
  TableBody,
  TableDataCell,
  TableEmptyState,
  TableFooter,
  TableFooterRow,
  TableHead,
  TableHeaderCell,
  TablePaginationWrapper,
  TableRow,
  PaginationTitle,
  PaginationRight,
  PaginationLeft,
  PaginationButton,
} from './table.style';
import { EmptyState, RightArrowDisabled, RightArrowPrimary } from '../../icons';
import { IntlMessages } from '../../../helper/langHelper';

const selectionHook = (hooks) => {
  hooks.allColumns.push((columns) => [
    {
      id: '_selector',
      disableResizing: true,
      disableGroupBy: true,
      minWidth: 45,
      width: 45,
      maxWidth: 45,
      Aggregated: undefined,
    },
    ...columns,
  ]);
  hooks.useInstanceBeforeDimensions.push(({ headerGroups }) => {
    // fix the parent group of the selection button to not be resizable
    const selectionGroupHeader = headerGroups[0].headers[0];
    selectionGroupHeader.canResize = false;
  });
};
// eslint-disable-next-line
const Table = forwardRef((props, ref) => {
  const {
    data,
    columns,
    hasFooter,
    onSortedChange,
    onRowClick,
    getTableRowProps,
    canSelect = false,
    onRowsSelect,
    isSortByApi = false,
    EmptyStateText = <IntlMessages id="common.empty.state" defaultMessage="No Record Found" />,
    actions = [],
    pageSize,
    page,
    prevPage,
    nextPage,
    total = 0,
    notRequireEmptyState = false,
    isActionsRenderConditinal = false,

    extraHeaderPadding = false,
  } = props;

  // pagination logic
  const maxPages = Math.ceil(total / pageSize);
  const start = page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, total);
  const hasPrev = page > 0;
  const hasNext = page + 1 < maxPages;

  const hooks = useMemo(() => {
    if (canSelect) {
      return [useSortBy, useRowSelect, selectionHook];
    }
    return [useSortBy, useRowSelect];
  }, [canSelect]);

  const instaneObj = useMemo(() => {
    if (isSortByApi) {
      return {
        columns,
        data,
        manualSortBy: true,
        autoResetSortBy: false,
      };
    }

    return {
      columns,
      data,
    };
  }, [columns, data, isSortByApi]);
  const instance = useTable(instaneObj, ...hooks);
  const {
    getTableProps,
    headerGroups,
    getTableBodyProps,
    rows,
    prepareRow,
    selectedFlatRows,
    state: { sortBy },
    toggleAllRowsSelected,
  } = instance;

  useImperativeHandle(ref, () => ({
    onToggleAllRowsSelected(value) {
      toggleAllRowsSelected(value);
    },
  }));
  useEffect(() => {
    if (onRowsSelect) {
      onRowsSelect(selectedFlatRows);
    }
  }, [onRowsSelect, selectedFlatRows]);

  useEffect(() => {
    if (onSortedChange) {
      onSortedChange(sortBy);
    }
  }, [sortBy, onSortedChange]);

  if (!data?.length) {
    return (
      <TableEmptyState>
        {!notRequireEmptyState ? (
          <>
            <EmptyStateContainer>
              <EmptyState />
            </EmptyStateContainer>
            <StyledEmptyStateText size="header3">{EmptyStateText}</StyledEmptyStateText>
          </>
        ) : null}
      </TableEmptyState>
    );
  }

  const cellClickHandler = (cell) => () => {
    onRowClick &&
      !cell.column.isGrouped &&
      !cell.row.isGrouped &&
      cell.column.id !== '_selector' &&
      onRowClick(cell.row.original, cell.column.id);
  };

  return (
    <>
      <StyledTable {...getTableProps()}>
        <TableHead notRequireEmptyState={notRequireEmptyState}>
          {headerGroups.map((headerGroup, headerIndex) => (
            <TableRow {...headerGroup.getHeaderGroupProps()} key={headerIndex}>
              {headerGroup.headers.map((column, rowIndex) => (
                <TableHeaderCell
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  // onClick={() => column.canSort && column.toggleSortBy(!column.isSortedDesc)}
                  key={rowIndex}
                  width={column?.width ? column?.width : null}
                  extraHeaderPadding={extraHeaderPadding}
                >
                  <HeaderCellWithIcon>
                    <span>{column.render('Header')}</span>
                    {/* {showSortIcons && column.Header !== '' ? (
                        <>
                          <SortIconWrapper>
                            <SortingIcon src={ascSortedActiveIconV3} alt="Desc down inactive" />
                            <SortingIcon src={decsSortedInActiveIconV3} alt="Desc down inactive" />
                          </SortIconWrapper>
                        </>
                      ) : null} */}
                  </HeaderCellWithIcon>
                  {/* {column.canSort ? renderSortContent(column) : null} */}
                </TableHeaderCell>
              ))}
            </TableRow>
          ))}
        </TableHead>

        <TableBody {...getTableBodyProps()}>
          {rows.map((row, rowIndex) => {
            prepareRow(row);
            return (
              <TableRow
                {...row.getRowProps()}
                hasFooter={hasFooter}
                key={rowIndex}
                {...(getTableRowProps ? { reportProps: getTableRowProps(row) } : {})}
                notRequireEmptyState={notRequireEmptyState}
              >
                {row.cells.map((cell, index) => {
                  return (
                    <TableDataCell
                      {...cell.getCellProps()}
                      key={index}
                      onClick={cellClickHandler(cell)}
                    >
                      {isActionsRenderConditinal ? (
                        <>
                          {cell?.column?.isAction ? (
                            <>
                              {cell.value !== 'OWNER'
                                ? actions.map((action) => action(cell.row.original, rowIndex))
                                : null}
                            </>
                          ) : (
                            <div>{cell.render('Cell')}</div>
                          )}
                        </>
                      ) : (
                        <>
                          {cell?.column?.isAction ? (
                            actions.map((action) => action(cell.row.original, rowIndex))
                          ) : (
                            <div>{cell.render('Cell')}</div>
                          )}
                        </>
                      )}

                      {/* {isActionsRenderConditinal ? (
                        <>
                          {cell?.column?.isAction ? (
                            <>
                              {!cell.value
                                ? actions.map((action) => action(cell.row.original, rowIndex))
                                : null}
                            </>
                          ) : (
                            <div>{cell.render('Cell')}</div>
                          )}
                        </>
                      ) : (
                        <>
                          {cell?.column?.isAction ? (
                            actions.map((action) => action(cell.row.original, rowIndex))
                          ) : (
                            <div>{cell.render('Cell')}</div>
                          )}
                        </>
                      )} */}
                    </TableDataCell>
                  );
                })}
              </TableRow>
            );
          })}
        </TableBody>

        {hasFooter && data.length > 0 && (
          <TableFooter>
            <TableFooterRow>
              <TablePaginationWrapper>
                <PaginationTitle>
                  <IntlMessages id="table.pagination.page" defaultMessage="Page" />: {start}-{end}{' '}
                  <IntlMessages id="nav.of" defaultMessage="of" /> {total}
                </PaginationTitle>
                <div
                  style={{
                    display: 'grid',
                    gridAutoFlow: 'column',
                    gap: '20px',
                    position: 'absolute',
                    right: '30px',
                  }}
                >
                  <PaginationLeft onClick={hasPrev ? prevPage : null}>
                    {hasPrev ? (
                      <img
                        src={RightArrowPrimary}
                        alt="RightArrowPrimary.svg"
                        style={{ transform: 'rotate(180deg)', width: '11px', height: '11px' }}
                      />
                    ) : (
                      <img
                        src={RightArrowDisabled}
                        alt="RightArrowPrimary.svg"
                        style={{ transform: 'rotate(180deg)', width: '11px', height: '11px' }}
                      />
                    )}
                    <PaginationButton color="lightGreen" cursor="pointer" disabled={!hasPrev}>
                      <IntlMessages id="nav.drop.down.previous" />
                    </PaginationButton>
                  </PaginationLeft>{' '}
                  <PaginationRight onClick={hasNext ? nextPage : null}>
                    <PaginationButton color="lightGreen" cursor="pointer" disabled={!hasNext}>
                      <IntlMessages id="nav.drop.down.next" />
                    </PaginationButton>
                    {hasNext ? (
                      <img
                        src={RightArrowPrimary}
                        alt="RightArrowPrimary.svg"
                        style={{ width: '11px', height: '11px' }}
                      />
                    ) : (
                      <img
                        src={RightArrowDisabled}
                        alt="RightArrowPrimary.svg"
                        style={{ width: '11px', height: '11px' }}
                      />
                    )}
                  </PaginationRight>
                </div>
              </TablePaginationWrapper>
            </TableFooterRow>
          </TableFooter>
        )}
      </StyledTable>
    </>
  );
});

export default Table;
